import {
  ComponentSize,
  type ComponentVariant,
} from 'src/modules/shared/types/component.type';

interface ButtonSkeletonProps {
  size?: ComponentSize;
  variant?: ComponentVariant;
  withIcon?: boolean;
  className?: string;
  fullWidth?: boolean;
}

/**
 * Skeleton loading state for button/dropdown components
 * Provides visual feedback while content is loading
 */
export const ButtonSkeleton: React.FC<ButtonSkeletonProps> = ({
  size = 'md',
  variant = 'primary',
  withIcon = false,
  className = '',
  fullWidth = false,
}) => {
  const variantClasses: Record<ComponentVariant, string> = {
    primary: 'bg-primary-200',
    secondary: 'bg-secondary-200',
    danger: 'bg-danger-200',
    neutral: 'bg-neutral-200',
    warning: 'bg-warning-200',
    success: 'bg-success-200',
    info: 'bg-info-200',
    whatsapp: 'bg-whatsapp-200',
    call: 'bg-call-200',
    email: 'bg-email-200',
  };

  const sizeClasses: Record<ComponentSize, string> = {
    '2xs': 'h-6 px-2',
    xs: 'h-7 px-2.5',
    sm: 'h-8 px-3',
    md: 'h-9 px-3.5',
    lg: 'h-10 px-4',
  };

  const contentSizeMap: Record<ComponentSize, string> = {
    '2xs': 'h-3 w-16',
    xs: 'h-3.5 w-20',
    sm: 'h-4 w-24',
    md: 'h-5 w-32',
    lg: 'h-5 w-40',
  };

  const iconSizeMap: Record<ComponentSize, string> = {
    '2xs': 'h-3 w-3',
    xs: 'h-3.5 w-3.5',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-5 w-5',
  };

  const bgClass = variantClasses[variant];
  const sizeClass = sizeClasses[size];
  const contentClass = contentSizeMap[size];
  const iconClass = iconSizeMap[size];

  return (
    <div
      className={`
        flex items-center justify-center gap-1.5 rounded-lg border border-gray-200
        animate-pulse
        ${sizeClass}
        ${fullWidth ? 'w-full' : 'w-fit'}
        ${className}
      `.trim()}
    >
      {withIcon && <div className={`${iconClass} ${bgClass} rounded`} />}
      <div className={`${contentClass} ${bgClass} rounded`} />
    </div>
  );
};
